import { FC, useState, useEffect, useCallback } from 'react';
import {
    Box,
    Avatar,
    Skeleton,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Grid,
    Chip,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
    BackgroundLabelQuery,
    BackgroundLabelItem,
} from '../../../../../../services/studio/StudioQuery/BackgroundLabel/BackgroundLabelQuery.services';
import './BackgroundLabelPopover.scss';

interface BackgroundLabelSliderProps {
    type: 'background' | 'label';
    onSelect: (item: BackgroundLabelItem) => void;
    selectedId?: string | null;
}

interface GroupedItems {
    [category: string]: BackgroundLabelItem[];
}

export const BackgroundLabelSlider: FC<BackgroundLabelSliderProps> = ({ type, onSelect, selectedId }) => {
    const { t } = useTranslation();
    const [groupedItems, setGroupedItems] = useState<GroupedItems>({});
    const [categories, setCategories] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const typeValue = type === 'background' ? 1 : 2;

    const fetchAllItems = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await BackgroundLabelQuery.GetBackgroundLabelList({
                page: 1,
                pageSize: 1000, // Tüm itemları çek
                type: typeValue,
            });

            const data = response?.data || response;
            const allItems = data?.items || [];

            // Kategorilere göre grupla
            const grouped: GroupedItems = {};
            const uniqueCategories: string[] = [];

            allItems.forEach((item: any) => {
                const category = item.category || t('general.uncategorized');
                if (!grouped[category]) {
                    grouped[category] = [];
                    uniqueCategories.push(category);
                }
                grouped[category].push(item);
            });

            // Her kategorideki itemları displayOrder'a göre sırala
            Object.keys(grouped).forEach((cat) => {
                grouped[cat].sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
            });

            setGroupedItems(grouped);
            setCategories(uniqueCategories);
        } catch (error) {
            console.error('Failed to fetch items:', error);
        } finally {
            setIsLoading(false);
        }
    }, [typeValue, t]);

    useEffect(() => {
        fetchAllItems();
    }, [type]); // eslint-disable-line react-hooks/exhaustive-deps

    if (isLoading) {
        return (
            <Box>
                <Grid container spacing={2}>
                    {Array.from({ length: 12 }).map((_, i) => (
                        <Grid
                            key={i}
                            size={{
                                xs: 3,
                            }}
                        >
                            <Skeleton variant="rectangular" width="100%" height={100} sx={{ borderRadius: 2 }} />
                        </Grid>
                    ))}
                </Grid>
            </Box>
        );
    }

    if (categories.length === 0) {
        return (
            <Box className="background-label-popover__empty">
                <Typography variant="caption">{t('mannequin.aiGeneration.popover.noItems')}</Typography>
            </Box>
        );
    }

    return (
        <Box>
            {categories.map((category) => {
                const categoryItems = groupedItems[category] || [];
                return (
                    <Accordion
                        key={category}
                        defaultExpanded={true}
                        sx={{
                            mb: 1,
                            '&:before': { display: 'none' },
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            borderRadius: '8px !important',
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMore />}
                            sx={{
                                '& .MuiAccordionSummary-content': {
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    pr: 2,
                                },
                            }}
                        >
                            <Typography variant="subtitle1" fontWeight="500">
                                {category}
                            </Typography>
                            <Chip
                                label={categoryItems.length}
                                size="small"
                                color="primary"
                                sx={{
                                    height: 24,
                                    fontSize: '0.75rem',
                                }}
                            />
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 2 }}>
                            {categoryItems.length > 0 ? (
                                <Grid container spacing={2}>
                                    {categoryItems.map((item) => (
                                        <Grid
                                            key={item.id}
                                            size={{
                                                xs: 3,
                                            }}
                                        >
                                            <Box
                                                className={`background-label-popover__grid-item ${
                                                    selectedId === item.id
                                                        ? 'background-label-popover__grid-item--selected'
                                                        : ''
                                                }`}
                                                onClick={() => onSelect(item)}
                                            >
                                                <Avatar
                                                    src={item.s3Url}
                                                    variant="rounded"
                                                    sx={{
                                                        width: '100%',
                                                        height: 100,
                                                        border: '2px solid #eee',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.2s ease',
                                                        '&:hover': {
                                                            transform: 'scale(1.05)',
                                                            borderColor: '#1976d2',
                                                        },
                                                    }}
                                                />
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        display: 'block',
                                                        mt: 0.5,
                                                        textAlign: 'center',
                                                        fontSize: '0.7rem',
                                                        color: '#666',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {item.name}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>
                            ) : (
                                <Typography variant="body2" color="text.secondary" textAlign="center">
                                    {t('mannequin.aiGeneration.popover.noItems')}
                                </Typography>
                            )}
                        </AccordionDetails>
                    </Accordion>
                );
            })}
        </Box>
    );
};
